import { Dispatch, PropsWithChildren, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

interface DefaultLayoutContextProps {
  isDrawerOpen: boolean;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  containerElementRef: RefObject<HTMLDivElement>;
  isLoadingMenu: boolean;
  setIsLoadingMenu: Dispatch<SetStateAction<boolean>>;
  isMenuOpen: boolean;
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
}

export type MenuItem = {
  id: string;
  name: string;
};

const DefaultLayoutContext = createContext<DefaultLayoutContextProps>(undefined!);

export const DefaultLayoutProvider = ({ children }: PropsWithChildren) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isLoadingMenu, setIsLoadingMenu] = useState<boolean>(false);
  const location = useLocation();
  const containerElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location]);

  const values = useMemo(
    () => ({
      isDrawerOpen,
      setIsDrawerOpen,
      containerElementRef,
      isLoadingMenu,
      setIsLoadingMenu,
      isMenuOpen,
      setIsMenuOpen,
    }),
    [isDrawerOpen, isLoadingMenu, isMenuOpen]
  );
  return <DefaultLayoutContext.Provider value={values}>{children}</DefaultLayoutContext.Provider>;
};

export function useDefaultLayoutContext(): DefaultLayoutContextProps {
  const context = useContext(DefaultLayoutContext);

  if (typeof context === 'undefined') {
    throw new Error('useDefaultLayoutContext should be used within the DefaultLayoutContext provider!');
  }

  return context;
}
